// AB#2112 — Super Admin activate / deactivate confirmation.
//
// Changing an account's state is never a single click. The dialog says what
// will happen (deactivation signs the account out on every device at once;
// activation needs a fresh sign-in — earlier sessions stay ended), and for an
// owner that the salon itself is not affected (account access and salon
// operational state are separate). A refusal from the server is explained in
// the admin's language, mapped from its stable errorCode.

import React, { useEffect, useState } from 'react';
import Button from '../../../components/Button/Button';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import { normalizeApiError } from '../../../types/api-errors';
import styles from './AccountStatusConfirmModal.module.css';

export interface AccountStatusConfirmModalProps {
    open: boolean;
    /** 'owner' adds the note that the salon is not affected. */
    kind: 'user' | 'owner';
    target: { id: number; fullName: string; email?: string };
    /** The state the admin is asking for. */
    nextActive: boolean;
    /** Sends the explicit state; resolves with whether anything changed. */
    onConfirm: () => Promise<{ changed: boolean }>;
    onClose: () => void;
    /** Called after a successful change so the page can refresh its list. */
    onChanged: () => void;
}

const REFUSAL_KEYS: Record<string, string> = {
    'ADMIN.SELF_DEACTIVATION': 'superadmin.accountStatus.error.self',
    'ADMIN.LAST_ACTIVE_SUPERADMIN': 'superadmin.accountStatus.error.lastSuperAdmin',
};

const AccountStatusConfirmModal: React.FC<AccountStatusConfirmModalProps> = ({
    open,
    kind,
    target,
    nextActive,
    onConfirm,
    onClose,
    onChanged,
}) => {
    const { t } = useLanguage();
    const { showToast } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [errorKey, setErrorKey] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setSubmitting(false);
            setErrorKey(null);
        }
    }, [open, target.id, nextActive]);

    if (!open) return null;

    const action = nextActive ? 'activate' : 'deactivate';

    const handleClose = () => {
        if (!submitting) onClose();
    };

    const handleConfirm = async () => {
        setSubmitting(true);
        setErrorKey(null);
        try {
            const { changed } = await onConfirm();
            showToast(
                t(changed ? `superadmin.accountStatus.toast.${action}d` : 'superadmin.accountStatus.toast.unchanged'),
                'success',
            );
            onChanged();
            onClose();
        } catch (err: unknown) {
            const { code, status } = normalizeApiError(err);
            setErrorKey(
                REFUSAL_KEYS[code] ??
                    (status === 404
                        ? 'superadmin.accountStatus.error.notFound'
                        : 'superadmin.accountStatus.error.generic'),
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.modalOverlay} role="presentation" onClick={handleClose}>
            <div
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="account-status-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h2 id="account-status-title" className={styles.modalTitle}>
                        {t(`superadmin.accountStatus.title.${action}`)}
                    </h2>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={handleClose}
                        aria-label={t('superadmin.accountStatus.action.close')}
                    >
                        &times;
                    </button>
                </div>

                <div className={styles.modalContent}>
                    <p className={styles.accountLine}>
                        {t('superadmin.accountStatus.account')}:{' '}
                        <span className={styles.accountName}>{target.fullName}</span>
                        {target.email ? (
                            <>
                                {' '}
                                (<bdi>{target.email}</bdi>)
                            </>
                        ) : null}
                    </p>
                    <p className={styles.body}>{t(`superadmin.accountStatus.body.${action}`)}</p>
                    {kind === 'owner' && !nextActive && (
                        <p className={styles.notice}>{t('superadmin.accountStatus.ownerNote')}</p>
                    )}
                    {errorKey && (
                        <p className={styles.error} role="alert">
                            {t(errorKey)}
                        </p>
                    )}
                </div>

                <div className={styles.modalActions}>
                    <Button
                        label={t('superadmin.accountStatus.action.cancel')}
                        onClick={handleClose}
                        variant="secondary"
                        disabled={submitting}
                    />
                    <Button
                        label={t(`superadmin.accountStatus.action.${action}`)}
                        onClick={handleConfirm}
                        variant="primary"
                        disabled={submitting}
                    />
                </div>
            </div>
        </div>
    );
};

export default AccountStatusConfirmModal;
